import styled from '@emotion/styled';
import { Link } from 'components/Link/Link';
import { Colors, Typography } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { UpNextArticle } from 'data/SingleArticle/types';
import paths from 'constants/paths';
import {
  ArticleImage,
  defaultImageQueryParams,
} from 'components/Images/ArticleImage/ArticleImage';
import { ImageQueryParams } from 'components/Images/RichImageComponent/RichImageComponent';
import { Spacer } from 'components/Spacer/Spacer';
import { withScrollTracking, TrackedComponent } from 'tracking';

const upNextImageParams: Readonly<ImageQueryParams> = {
  ...defaultImageQueryParams,
  w: '1200',
};

export const UpNextCard = ({ article }: { article: UpNextArticle }) => {
  const trackEvent = useTrackEvent();

  const { category, image, slug, shortDescription, title } = article;

  return (
    <div data-testid="upNext-card">
      <LinkWrapper>
        <Link
          data-testid="upNext-articleLink"
          href={paths.blogArticle(slug)}
          onClick={(event) =>
            trackEvent('linkClicked', {
              component: 'Up Next',
              linkText: title,
              href: paths.blogArticle(slug),
              path: window.location.pathname,
              posYFromTop: event.clientY,
            })
          }
        >
          <TrackingWrapper
            avroIdentifier={TrackedComponent.UpNext}
            preAvroIdentifier="Up Next"
          >
            <Image image={image} alt={title} queryParams={upNextImageParams} />
            <UpNextTag>Up next</UpNextTag>
            <TextWrapper>
              <Category>{category}</Category>
              <Title>{title}</Title>
              <ShortDescription>{shortDescription}</ShortDescription>
            </TextWrapper>
          </TrackingWrapper>
        </Link>
      </LinkWrapper>
      <Spacer hasLine={true} />
    </div>
  );
};

const Title = styled.strong`
  color: ${Colors.neutral800};
  margin-bottom: 10px;
  ${Typography.pageHeader9}
`;

const LinkWrapper = styled.div`
  color: ${Colors.neutral800};
  &:hover,
  &:focus {
    & ${Title} {
      color: ${Colors.blueJeans};
    }
  }
`;

const Outer = styled.section`
  display: grid;
  position: relative;
  ${mediaQueries.tablet} {
    grid-template-columns: 300px 1fr;
    grid-gap: 30px;
  }
`;

const TrackingWrapper = withScrollTracking(Outer);

const Image = styled(ArticleImage)`
  height: 200px;
  margin-bottom: 20px;
  width: 300px;
  object-fit: contain;
  ${mediaQueries.desktop} {
    margin-bottom: 0;
  }
`;

const UpNextTag = styled.div`
  align-items: center;
  background: ${Colors.bolt};
  border: 1px solid ${Colors.neutral700};
  border-radius: 3px;
  color: ${Colors.neutral800};
  display: flex;
  height: 30px;
  justify-content: center;
  left: 10px;
  position: absolute;
  text-transform: none;
  top: 10px;
  width: 73px;
  ${Typography.smallPrint1Bold}
`;

const TextWrapper = styled.div`
  align-self: center;
`;

const Category = styled.p`
  color: ${Colors.blueJeans};
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  margin-bottom: 10px;
`;

const ShortDescription = styled.p`
  color: ${Colors.neutral700};
  ${Typography.paragraph3}
`;
